import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DocumentInput from '../components/DocumentInput';
import PhoneInput from '../components/PhoneInput';
import { AddressFields, ADDRESS_KEYS } from '../components/AddressFields';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadPublicForm() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, vehicle: null, expiresAt: null });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emptyAddr = ADDRESS_KEYS.reduce((acc, k) => { acc[k] = ''; return acc; }, {});
  const [form, setForm] = useState({
    type: 'PF',
    name: '',
    document: '',
    phone: '',
    email: '',
    ...emptyAddr,
  });

  useEffect(() => {
    document.title = 'Dados do comprador';
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/lead/${token}`);
        setState({ loading: false, error: null, vehicle: data.vehicle, expiresAt: data.expires_at });
      } catch (err) {
        setState({
          loading: false,
          error: err?.response?.data?.detail || 'Link inválido ou expirado.',
          vehicle: null, expiresAt: null,
        });
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    if (!form.document.trim()) { toast.error('CPF/CNPJ é obrigatório'); return; }
    setSubmitting(true);
    try {
      const payload = {
        type: form.type,
        name: form.name.trim(),
        document: form.document,
        phone: form.phone || null,
        email: form.email.trim() || null,
      };
      for (const k of ADDRESS_KEYS) {
        payload[k] = (form[k] || '').trim() || null;
      }
      await axios.post(`${API_BASE}/public/lead/${token}`, payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar dados');
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Verificando link…</div>;
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-5xl mb-3">⚠️</div>
          <h1 className="text-xl font-semibold mb-2">{state.error}</h1>
          <p className="text-sm text-muted-foreground">Peça à empresa que envie um novo link.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-5xl mb-3">✅</div>
          <h1 className="text-xl font-semibold mb-2">Dados enviados com sucesso</h1>
          <p className="text-sm text-muted-foreground">Já avisamos a empresa. Você pode fechar esta página.</p>
        </div>
      </div>
    );
  }

  const expiresLabel = state.expiresAt ? new Date(state.expiresAt).toLocaleString('pt-BR') : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-slate-900 text-white px-6 py-5">
          <div className="text-xs uppercase tracking-widest text-slate-300">Matelli Motors</div>
          <h1 className="text-xl font-semibold mt-1">Cadastro de comprador</h1>
          {state.vehicle && (
            <div className="text-sm text-slate-300 mt-2">
              Para o veículo <strong>{state.vehicle.brand} {state.vehicle.model} {state.vehicle.year}</strong>
              {state.vehicle.plate ? ` · Placa ${state.vehicle.plate}` : ''}
            </div>
          )}
          {expiresLabel && (
            <div className="text-xs text-amber-200 mt-2">⏱️ Link válido até {expiresLabel}</div>
          )}
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Preencha seus dados abaixo. Eles ficarão automaticamente vinculados à sua compra.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="lead-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PF">Pessoa Física</SelectItem>
                  <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Nome / Razão social *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="lead-name" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>CPF / CNPJ *</Label>
              <DocumentInput value={form.document} onChange={(v) => setForm((f) => ({ ...f, document: v }))} data-testid="lead-document" />
            </div>
            <div>
              <Label>Telefone</Label>
              <PhoneInput value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} data-testid="lead-phone" />
            </div>
          </div>
          <div>
            <Label>E-mail</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="lead-email" />
          </div>
          <div className="pt-2 border-t">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Endereço</div>
            <AddressFields
              value={form}
              onChange={(next) => setForm((f) => ({ ...f, ...next }))}
              testIdPrefix="lead-addr"
            />
          </div>
          <div className="pt-3">
            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="lead-submit"
            >
              {submitting ? 'Enviando…' : 'Enviar dados'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
